import React, { isValidElement, useState } from 'react';
import JsonViewer from '@/components/JsonViewer';
import Triangle from '@/components/Triangle';
import IconV2 from '@/components/v2/IconV2';

const Title = ({ className, children, onClick }) => {
  if (!children) {
    return null;
  }
  return (
    <h2 className={`title ${className || ''}`} onClick={onClick}>
      {children}
    </h2>
  );
};

const Subtitle = ({ className, children }) => {
  if (!children) {
    return null;
  }
  return <h3 className={`subtitle ${className || ''}`}>{children}</h3>;
};

const Description = ({ className, children }) => {
  if (!children) {
    return null;
  }
  return <p className={`description ${className || ''}`}>{children}</p>;
};

const Link = ({ url, target = '_blank', rel = 'noreferrer', className, children }) => {
  if (!children) {
    return null;
  }
  return (
    <a href={url} target={target} rel={rel} className={`link ${className || ''}`}>
      {children}
    </a>
  );
};

const Row = ({ className, children }) => {
  if (!children) {
    return null;
  }
  return <div className={`row ${className || ''}`}>{children}</div>;
};

const Left = ({ className, children }) => {
  if (!children) {
    return null;
  }
  return <div className={`row-left ${className || ''}`}>{children}</div>;
};

const Right = ({ className, children }) => {
  if (!children) {
    return null;
  }
  return <div className={`row-right ${className || ''}`}>{children}</div>;
};

const Banner = ({ icon, text, className }) => {
  if (!text) {
    return null;
  }
  return (
    <div className={`banner ${className || ''}`}>
      {icon && !isValidElement(icon) && <IconV2>{icon()}</IconV2>}
      <span>{text}</span>
    </div>
  );
};

const Fields = ({ title, fields, className }) => {
  if (!fields) {
    return null;
  }
  return (
    <OutputViewerSection className={className}>
      <Title>{title}</Title>
      <div className={'fields'}>
        {Object.entries(fields).map(([key, value]) => (
          <Row key={key} className={'field'}>
            <Left>
              <Title>{`${key}:`}</Title>
            </Left>
            <Right>{value}</Right>
          </Row>
        ))}
      </div>
    </OutputViewerSection>
  );
};

const CodeBlock = ({ code, className, collapsed }) => {
  if (!code) {
    return null;
  }
  return (
    <div className={`output-viewer-code-block ${className || ''}`}>
      <JsonViewer json={code} collapsed={collapsed} objectSortKeys={false} />
    </div>
  );
};

const Grid = ({ title, items, className, collapsable }) => {
  const [collapsed, setCollapsed] = useState(false);

  const handleTitleClick = () => {
    if (collapsable) {
      setCollapsed(!collapsed);
    }
  };

  // Items is an array of key/value pairs
  if (!items || !Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <div className={`output-viewer-grid ${collapsed ? 'collapsed' : ''} ${className || ''}`}>
      <Title className={'grid-title'} onClick={handleTitleClick}>
        {collapsable && <Triangle isOpen={!collapsed} />}
        {title}
      </Title>
      {!collapsed && (
        <div className="grid-items">
          {items.map(({ key, value }) => (
            <React.Fragment key={key}>
              <Title>{`${key}:`}</Title>
              <Description>{value}</Description>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// items are [{ key, label, children }]
const Tabs = ({ items, className }) => {
  const [activeTab, setActiveTab] = useState(items[0]?.key);

  const activeChildren = items?.find((item) => item.key === activeTab)?.children;

  const handleTabClick = (key) => {
    setActiveTab(key);
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`output-viewer-tabs-container ${className || ''}`}>
      <div className="output-viewer-tabs">
        {items.map(({ key, label }) => (
          <button key={key} className={`output-viewer-tab-button ${activeTab === key ? 'active' : ''}`} onClick={() => handleTabClick(key)}>
            {label}
          </button>
        ))}
      </div>
      <div className="output-viewer-tab-content">{activeChildren}</div>
    </div>
  );
};

const OutputViewerSection = ({ className, children }) => {
  if (!children) {
    return null;
  }
  return <div className={`output-viewer-section ${className || ''}`}>{children}</div>;
};

OutputViewerSection.Title = Title;
OutputViewerSection.Subtitle = Subtitle;
OutputViewerSection.Description = Description;
OutputViewerSection.Link = Link;
OutputViewerSection.Row = Row;
OutputViewerSection.Row.Left = Left;
OutputViewerSection.Row.Right = Right;
OutputViewerSection.Fields = Fields;
OutputViewerSection.CodeBlock = CodeBlock;
OutputViewerSection.Grid = Grid;
OutputViewerSection.Tabs = Tabs;
OutputViewerSection.Banner = Banner;

export default OutputViewerSection;
